import { buildCalculatorBreakdown, formatPlotAcreageBadge } from './plantingAssistantUtils';
import { hydrateWizardAnswers } from './plantingWizardUtils';
import {
  buildPlanViewModel,
  catalogBlendDisplayName,
  planPreviewRequest,
  sanitizeBlendKey,
} from './plantingPlanDisplay';
import { placeFromSavedGroup } from './plantingMarkPlantedUtils';
import { getPlantingSchedule } from './plantingTimingUtils';
import { resolvePlanAcres, stripPlanNameAcres } from './plantingPlansLibraryUtils';

const MONTH_SHORT = Object.freeze([
  'JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN',
  'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC',
]);

export function resolveOptionLabel(options, id) {
  if (!id || !Array.isArray(options)) return '';
  const match = options.find((opt) => opt.id === id || opt.value === id);
  return match?.label || match?.name || '';
}

export function formatExportDate(date = new Date()) {
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(date);
}

export function formatUserDisplayName(profile = {}) {
  const full = [profile.first_name, profile.last_name].filter(Boolean).join(' ').trim();
  return full || profile.username || '';
}

export function buildPlantingExportFilename(groupName = 'estimate', date = new Date()) {
  const slug = String(groupName)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40) || 'estimate';
  const iso = date.toISOString().slice(0, 10);
  return `yona-planting-estimate-${slug}-${iso}.pdf`;
}

/**
 * Normalized payload for Planting Assistant PDF export.
 * Stores omitted by design — user may take this to any store.
 */
export function buildPlantingExportPayload({
  generatedAt = new Date(),
  userDisplayName = '',
  groupName = '',
  groupNote = '',
  clubName = '',
  isClubGroup = false,
  zoneLabel = '',
  soilTypeLabel = '',
  soilDrainageLabel = '',
  soilPh = '',
  soilNotes = '',
  plots = [],
  totalAcres = 0,
  selectedSeedId = '',
  selectedFertilizerId = '',
  seedOptions = [],
  fertilizerOptions = [],
  recommendationResult = null,
} = {}) {
  const seedPreset = seedOptions.find((p) => p.id === selectedSeedId);
  const fertilizerPreset = fertilizerOptions.find((p) => p.id === selectedFertilizerId);
  const breakdown = buildCalculatorBreakdown({
    acres: totalAcres,
    seedPreset,
    fertilizerPreset,
    seedId: selectedSeedId,
    fertilizerId: selectedFertilizerId,
    recommendationResult,
  });

  const hasMaterial = Boolean(breakdown.seed || breakdown.fertilizer);
  const ratesAreAdjusted = Boolean(
    (breakdown.seed && !breakdown.seed.isPreview)
    || (breakdown.fertilizer && !breakdown.fertilizer.isPreview)
  );

  return {
    generatedAt: generatedAt instanceof Date ? generatedAt.toISOString() : generatedAt,
    generatedAtLabel: formatExportDate(generatedAt instanceof Date ? generatedAt : new Date(generatedAt)),
    userDisplayName: userDisplayName.trim(),
    groupName: groupName.trim(),
    groupNote: (groupNote || '').trim(),
    clubName: (clubName || '').trim(),
    isClubGroup: Boolean(isClubGroup && clubName),
    zoneLabel: (zoneLabel || '').trim(),
    soilTypeLabel: (soilTypeLabel || '').trim(),
    soilDrainageLabel: (soilDrainageLabel || '').trim(),
    soilPh: soilPh !== null && soilPh !== undefined ? String(soilPh).trim() : '',
    soilNotes: (soilNotes || '').trim(),
    plots: plots.map((plot) => ({
      name: plot.name || 'Unnamed plot',
      acres: typeof plot.acreage === 'number' ? formatPlotAcreageBadge(plot.acreage) : '—',
      location: plot.location || '',
    })),
    totalAcres: formatPlotAcreageBadge(totalAcres),
    breakdown,
    hasMaterial,
    ratesAreAdjusted,
    filename: buildPlantingExportFilename(groupName, generatedAt instanceof Date ? generatedAt : new Date(generatedAt)),
  };
}

export function canShowPlantingExport({
  calculatorMode = 'compose',
  selectedSeedId = '',
  selectedFertilizerId = '',
  selectedPolygonCount = 0,
} = {}) {
  if (calculatorMode === 'compose' || selectedPolygonCount === 0) return false;
  return Boolean(selectedSeedId || selectedFertilizerId);
}

export function canExportSavedPlan(group) {
  return Boolean(group && group.id != null);
}

export function resolveGroupPlotsForExport(group, polygons = []) {
  const ids = new Set([
    ...(group?.personal_polygon_ids || []).map((id) => String(id)),
    ...(group?.club_boundary_ids || []).map((id) => String(id)),
  ]);
  if (!ids.size) return [];
  return (Array.isArray(polygons) ? polygons : []).filter((plot) => ids.has(String(plot.id)));
}

export function formatExportWindowLabel(startIso, endIso) {
  const start = formatExportMonthDay(startIso);
  const end = formatExportMonthDay(endIso);
  if (start && end) return `${start} – ${end}`;
  return start || end || '';
}

function formatExportMonthDay(iso) {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || '').trim());
  if (!match) return '';
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (!month || !day) return '';
  return `${MONTH_SHORT[month - 1]} ${day}`;
}

/**
 * Single-plan PDF payload from a saved Field Edition AreaGroup.
 * Mix/cart come from plan-preview, the same engine VIEW uses.
 */
export async function buildFieldEditionExportPayload({
  group = null,
  generatedAt = new Date(),
  userDisplayName = '',
  plots = [],
  clubName = '',
  zoneLabel = '',
  soilTypeLabel = '',
  soilDrainageLabel = '',
  loadPlan,
} = {}) {
  const answers = hydrateWizardAnswers(group?.wizard_answers, group?.planting_season);
  const preferKey = sanitizeBlendKey(group?.wizard_answers?.blend);
  const acres = resolvePlanAcres(group);
  const place = placeFromSavedGroup(group);
  let preview = null;
  if (typeof loadPlan === 'function') {
    try {
      preview = await loadPlan(planPreviewRequest({
        answers,
        place,
        acres,
        blend: preferKey,
        draftSoilPh: group?.soil_ph,
      }));
    } catch (err) {
      console.error('Plan preview for export failed', err);
    }
  }
  const model = buildPlanViewModel(preview, {
    zone: zoneLabel || group?.usda_zone_code,
  }) || {
    recName: catalogBlendDisplayName(preferKey),
    cart: [],
    totalLbs: 0,
  };
  const groupName = stripPlanNameAcres(group?.name) || 'Untitled plan';
  const season = String(group?.planting_season || answers?.season || '').trim();
  const windowLabel = formatExportWindowLabel(group?.window_start, group?.window_end)
    || getPlantingSchedule(answers)?.windowLabel
    || '';
  const frostLast = String(group?.last_frost_label || '').trim();
  const frostFirst = String(group?.first_frost_label || '').trim();
  const frostLabel = [frostLast, frostFirst].filter(Boolean).join(' – ');
  const plotRows = (Array.isArray(plots) ? plots : []).map((plot) => ({
    name: plot.name || 'Unnamed plot',
    acres: typeof plot.acreage === 'number' ? formatPlotAcreageBadge(plot.acreage) : '—',
    location: plot.location || '',
  }));
  const zone = String(zoneLabel || group?.usda_zone_code || '').trim();
  const at = generatedAt instanceof Date ? generatedAt : new Date(generatedAt);
  const materials = (model.cart || []).map((item) => ({
    name: item.name || '',
    rate: item.rate || '',
    qty: item.qty || '',
    bags: item.bags || '',
  }));

  return {
    kind: 'field-edition',
    generatedAt: at.toISOString(),
    generatedAtLabel: formatExportDate(at),
    userDisplayName: String(userDisplayName || '').trim(),
    groupName,
    groupNote: String(group?.note || '').trim(),
    placeLabel: String(group?.place_label || '').trim(),
    clubName: String(clubName || '').trim(),
    isClubGroup: Boolean(group?.club && clubName),
    zoneLabel: zone,
    soilTypeLabel: String(soilTypeLabel || '').trim(),
    soilDrainageLabel: String(soilDrainageLabel || '').trim(),
    soilPh: group?.soil_ph !== null && group?.soil_ph !== undefined && group?.soil_ph !== ''
      ? String(group.soil_ph).trim()
      : '',
    soilNotes: String(group?.soil_notes || '').trim(),
    plots: plotRows,
    plotCount: Number(group?.polygon_count) || plotRows.length,
    totalAcres: acres > 0 ? (Math.round(acres * 10) / 10).toFixed(1) : '—',
    mixName: model.recName || '',
    season,
    windowLabel,
    frostLast,
    frostFirst,
    frostLabel,
    materials,
    hasMaterial: materials.length > 0,
    totalMaterialLabel: Number.isFinite(model.totalLbs) ? `${model.totalLbs} lbs` : '',
    filename: buildPlantingExportFilename(groupName, at),
    breakdown: { seed: null, fertilizer: null, totalLbs: model.totalLbs || 0 },
    ratesAreAdjusted: true,
  };
}
